import React, { useEffect, useMemo, useState } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';
const currentMonth = () => new Date().toISOString().slice(0, 7);
const num = (n) => n == null ? '—' : Number(n).toLocaleString('tr-TR', { maximumFractionDigits: 3 });
const cell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
const base = { padding: 12, border: '1px solid #e2e8f0', borderRadius: 12, background: '#fff' };
const th = { padding: 10, borderBottom: '1px solid #cbd5e1', textAlign: 'right', whiteSpace: 'nowrap' };
const td = { padding: 10, borderBottom: '1px solid #e2e8f0', textAlign: 'right' };

function changeMonth(value, offset) {
  const [year, month] = value.split('-').map(Number);
  const next = new Date(year, month - 1 + offset, 1);
  return `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}`;
}

function exportCsv(rows, mode, month) {
  const columns = mode === 'firma'
    ? ['Firma', 'Ürün', 'Birim', 'LOGO', 'ERP Hareket', 'Fark', 'Durum']
    : ['Ürün', 'Firma', 'Birim', 'LOGO', 'ERP Hareket', 'Fire', 'Zayi', 'İkram', 'Fark', 'Durum'];
  const values = rows.map(r => mode === 'firma'
    ? [r.firma_ad, r.malzeme_ad, r.birim, r.logo_miktar, r.kontrol_tipi === 'TRANSFER' ? r.transfer_miktar : r.stok_miktar, r.fark, r.durum]
    : [r.malzeme_ad, r.firma_ad, r.birim, r.logo_miktar, r.kontrol_tipi === 'TRANSFER' ? r.transfer_miktar : r.stok_miktar, r.fire_miktar, r.zayi_miktar, r.ikram_miktar, r.fark, r.durum]);
  const csv = '\uFEFF' + [columns, ...values].map(r => r.map(cell).join(';')).join('\r\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a'); a.href = url; a.download = `logo-transfer-${mode}-${month}.csv`;
  document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
}

export default function LogoTransferMutabakatPage() {
  const [month, setMonth] = useState(currentMonth);
  const [mode, setMode] = useState('firma');
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [showInvoices, setShowInvoices] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      setLoading(true); setError(''); setRows([]); setInvoices([]);
      try {
        const token = localStorage.getItem('access_token');
        const response = await fetch(`${API_URL}/api/raporlar/logo-transfer-mutabakat?ay=${encodeURIComponent(month)}`, {
          signal: controller.signal,
          headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), Accept: 'application/json' },
        });
        if (!response.ok) throw new Error(`Rapor alınamadı (HTTP ${response.status})`);
        const payload = await response.json();
        if (!Array.isArray(payload.items)) throw new Error('API beklenen items listesini döndürmedi');
        if (!controller.signal.aborted) { setRows(payload.items); setInvoices(Array.isArray(payload.faturalar) ? payload.faturalar : []); }
      } catch (err) {
        if (err.name !== 'AbortError') setError(err.message || 'Veriler alınamadı');
      } finally { if (!controller.signal.aborted) setLoading(false); }
    };
    load(); return () => controller.abort();
  }, [month]);

  const filtered = useMemo(() => rows.filter(r => `${r.firma_ad} ${r.malzeme_ad} ${r.malzeme_kod || ''}`.toLocaleLowerCase('tr-TR').includes(query.toLocaleLowerCase('tr-TR'))), [rows, query]);
  const groups = useMemo(() => {
    const grouped = new Map();
    for (const r of filtered) {
      const id = mode === 'firma' ? `f:${r.firma_id}` : `m:${r.malzeme_id}:${r.birim}`;
      if (!grouped.has(id)) grouped.set(id, { id, name: mode === 'firma' ? r.firma_ad : r.malzeme_ad, items: [] });
      grouped.get(id).items.push(r);
    }
    return [...grouped.values()].sort((a, b) => a.name.localeCompare(b.name, 'tr'));
  }, [filtered, mode]);

  const invoiceGroups = useMemo(() => {
    const m = new Map();
    for (const f of invoices) {
      const key = `f:${f.firma_id}`;
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(f);
    }
    return m;
  }, [invoices]);
  const money = (v) => v == null ? '—' : Number(v).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div style={{ padding: 20, maxWidth: 1200, margin: 'auto', color: '#0f172a' }}>
      <h2 style={{ marginTop: 0 }}>LOGO / Stok Transfer Mutabakatı</h2>
      <div style={{ ...base, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button type="button" onClick={() => setMonth(m => changeMonth(m, -1))}>‹</button>
          <input aria-label="Rapor ayı" type="month" value={month} onChange={e => setMonth(e.target.value)} />
          <button type="button" onClick={() => setMonth(m => changeMonth(m, 1))}>›</button>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {[['firma', 'Firma Bazında'], ['urun', 'Ürün Bazında']].map(([key, label]) => (
            <button key={key} type="button" onClick={() => { setMode(key); setExpanded({}); }}
              style={{ border: '1px solid #cbd5e1', padding: '9px 13px', borderRadius: 9, fontWeight: 700, background: mode === key ? '#dbeafe' : '#fff' }}>{label}</button>
          ))}
        </div>
        <input placeholder="Firma veya ürün ara..." value={query} onChange={e => setQuery(e.target.value)} style={{ padding: 9, minWidth: 210 }} />
        <button type="button" disabled={!filtered.length} onClick={() => exportCsv(filtered, mode, month)}>CSV Olarak İndir</button>
      </div>
      {error && <div role="alert" style={{ ...base, color: '#991b1b', marginBottom: 12 }}>{error}</div>}
      {!loading && <div style={{ ...base, marginBottom: 16 }}>
        <button type="button" onClick={() => setShowInvoices(v => !v)} style={{ fontWeight: 700, cursor: 'pointer', border: 0, background: 'transparent' }}>
          {showInvoices ? '▼' : '▶'} LOGO Giden Faturalar ({invoices.length})
        </button>
        {showInvoices && (invoices.length === 0 ? <p>Fatura listesi boş veya backend henüz yeni sürümde değil.</p> :
          invoices.filter(f => `${f.firma_ad} ${f.fatura_no}`.toLocaleLowerCase('tr-TR').includes(query.toLocaleLowerCase('tr-TR'))).map((f, idx) =>
            <details key={`${f.fatura_no}-${idx}`} style={{ padding: 10, borderTop: '1px solid #e2e8f0' }}>
              <summary style={{ cursor: 'pointer' }}><strong>{f.fatura_no}</strong> · {f.fatura_tarihi} · {f.firma_ad} · {(f.kalemler || []).length} kalem · {f.durum}</summary>
              {f.cari_mutabakat && <div style={{ margin: '10px 0', padding: 12, borderRadius: 8, background: '#f1f5f9', display: 'flex', flexWrap: 'wrap', gap: 18 }}>
                <span><strong>Kontrol:</strong> {f.cari_mutabakat.tur === 'TRANSFER' ? 'İç transfer' : 'Cari alacak'}</span>
                <span><strong>LOGO:</strong> {num(f.logo_toplam)} TL</span>
                {f.cari_mutabakat.tur === 'CARI' && <>
                  <span><strong>ERP faturası:</strong> {num(f.cari_mutabakat.erp_fatura_tutar)} TL</span>
                  <span><strong>ERP alacak:</strong> {num(f.cari_mutabakat.erp_alacak_tutar)} TL</span>
                  <span><strong>Fark:</strong> {num(f.cari_mutabakat.fark)} TL</span>
                </>}
                <span><strong>Durum:</strong> {f.cari_mutabakat.durum}</span>
              </div>}
              {f.stok_mutabakat && <div style={{ margin: '10px 0', padding: 12, border: '1px solid #cbd5e1', borderRadius: 8 }}>
                <strong>ERP Stok Hareketleri</strong> · {f.stok_mutabakat.durum}
                <p style={{ fontSize: 12, color: '#64748b' }}>{f.stok_mutabakat.notlar}</p>
                {(f.stok_mutabakat.hareketler || []).length > 0 && <div style={{ overflowX: 'auto' }}><table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr><th style={{ ...th, textAlign: 'left' }}>Tarih</th><th style={{ ...th, textAlign: 'left' }}>ERP Malzeme</th><th style={th}>Miktar</th><th style={th}>Birim</th><th style={th}>Hareket ID</th></tr></thead>
                  <tbody>{f.stok_mutabakat.hareketler.map((h, i) => <tr key={i}><td style={{ ...td, textAlign: 'left' }}>{h.hareket_tarihi}</td><td style={{ ...td, textAlign: 'left' }}>{h.malzeme_ad}</td><td style={td}>{num(h.miktar)}</td><td style={td}>{h.birim}</td><td style={td}>{h.hareket_id}</td></tr>)}</tbody>
                </table></div>}
              </div>}
              {(f.kalemler || []).length ? <div style={{ overflowX: 'auto' }}><table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={{ ...th, textAlign: 'left' }}>LOGO ürünü</th><th style={{ ...th, textAlign: 'left' }}>ERP karşılığı</th><th style={th}>Miktar</th><th style={th}>Birim</th><th style={th}>ERP stok</th><th style={th}>Durum</th></tr></thead>
                <tbody>{f.kalemler.map((k, i) => <tr key={i}><td style={{ ...td, textAlign: 'left' }}>{k.malzeme_ad}</td><td style={{ ...td, textAlign: 'left' }}>{k.erp_malzeme_ad || '—'}</td><td style={td}>{num(k.logo_miktar)}</td><td style={td}>{k.birim}</td><td style={td}>{num(k.erp_stok_miktar)}</td><td style={td}>{k.durum}</td></tr>)}</tbody>
              </table></div> : <p>Fatura detay kalemleri alınamadı.</p>}
            </details>))}
      </div>}
      {loading ? <div style={base}>Rapor hazırlanıyor...</div> : groups.length === 0 ? <div style={base}>Bu dönem için kayıt bulunamadı.</div> : groups.map(g => (
        <div key={g.id} style={{ ...base, padding: 0, marginBottom: 12, overflow: 'hidden' }}>
          <button type="button" onClick={() => setExpanded(prev => ({ ...prev, [g.id]: !prev[g.id] }))}
            style={{ display: 'flex', justifyContent: 'space-between', width: '100%', textAlign: 'left', border: 0, padding: 16, background: '#f8fafc', cursor: 'pointer' }}>
            <strong>{expanded[g.id] ? '▼' : '▶'} {g.name}</strong>
            <span>{g.items.length} kayıt · {g.items.some(r => r.durum !== 'ESLESTI') ? 'Fark / kontrol var' : 'Eşleşti'}</span>
          </button>
          {expanded[g.id] && mode === 'firma' && g.items[0]?.kontrol_tipi !== 'TRANSFER' && <div style={{ padding: 14, borderBottom: '1px solid #e2e8f0' }}>
             <strong>Cari Hesap Mutabakatı (Fatura Bazında)</strong>
             <div style={{ overflowX: 'auto', marginTop: 8 }}><table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 750 }}>
               <thead><tr><th style={{...th, textAlign:'left'}}>Fatura No</th><th style={th}>LOGO (TL)</th><th style={th}>ERP Fatura (TL)</th><th style={th}>ERP Alacak (TL)</th><th style={th}>Cari Fark (TL)</th><th style={th}>Durum</th></tr></thead>
               <tbody>{(invoiceGroups.get(g.id) || []).map((f, i) => <tr key={`${f.fatura_no}-${i}`}>
                 <td style={{...td, textAlign:'left'}}>{f.fatura_no}</td><td style={td}>{money(f.logo_toplam)}</td>
                 <td style={td}>{money(f.cari_mutabakat?.erp_fatura_tutar)}</td><td style={td}>{money(f.cari_mutabakat?.erp_alacak_tutar)}</td>
                 <td style={td}>{money(f.cari_mutabakat?.fark)}</td><td style={td}>{f.cari_mutabakat?.durum || 'KONTROL'}</td>
               </tr>)}</tbody>
             </table></div>
             <p style={{ fontSize: 12, color: '#64748b' }}>Cari karşılaştırması kalan bakiye değil, faturadan doğan orijinal alacak üzerinden yapılır. ERP stok çıkışı yalnız ilgili satış faturasına bağlı SATIS_DIS hareketlerinden hesaplanır.</p>
           </div>}
           {expanded[g.id] && <div style={{ overflowX: 'auto' }}><table style={{ borderCollapse: 'collapse', width: '100%', minWidth: mode === 'urun' ? 900 : 650 }}>
            <thead><tr><th style={{ ...th, textAlign: 'left' }}>{mode === 'firma' ? 'Ürün' : 'Firma'}</th><th style={th}>Birim</th><th style={th}>LOGO</th><th style={th}>{mode === 'firma' ? (g.items[0]?.kontrol_tipi === 'TRANSFER' ? 'Transfer' : 'ERP Stok Çıkışı') : 'ERP Hareket'}</th>
              {mode === 'urun' && <><th style={th}>Fire</th><th style={th}>Zayi</th><th style={th}>İkram</th></>}
              <th style={th}>Fark</th><th style={th}>Durum</th></tr></thead>
            <tbody>{g.items.map((r, i) => <tr key={`${r.firma_id}-${r.malzeme_id}-${r.birim}-${i}`}>
              <td style={{ ...td, textAlign: 'left' }}><strong>{mode === 'firma' ? r.malzeme_ad : r.firma_ad}</strong></td>
              <td style={td}>{r.birim}</td><td style={td}>{num(r.logo_miktar)}</td><td style={td}>{num(r.kontrol_tipi === 'TRANSFER' ? r.transfer_miktar : r.stok_miktar)}</td>
              {mode === 'urun' && <><td style={td}>{num(r.fire_miktar)}</td><td style={td}>{num(r.zayi_miktar)}</td><td style={td}>{num(r.ikram_miktar)}</td></>}
              <td style={{ ...td, color: r.fark == null ? '#64748b' : Math.abs(Number(r.fark)) < 0.0001 ? '#15803d' : '#b91c1c', fontWeight: 700 }}>{num(r.fark)}</td>
              <td style={td}>{r.durum || 'KONTROL'}</td>
            </tr>)}</tbody></table></div>}
        </div>
      ))}
      <p style={{ color: '#64748b', fontSize: 12 }}>Fark = LOGO − ERP hareketi (iç firmalarda transfer, dış müşterilerde SATIS_DIS). Fire, zayi ve ikram yalnızca ürün görünümünde bilgilendirme amaçlıdır; farktan düşülmez. Eksik veya eşleştirilemeyen miktarlar sıfır kabul edilmemelidir.</p>
    </div>
  );
}
